import React from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Grid } from '../../design-system'
import { Trophy, Share2, RotateCcw, TrendingUp, Clock, Target, CheckCircle2 } from 'lucide-react'

// Test Type Label Component
const TestTypeLabel = ({ testType, totalQuestions }) => {
  const typeConfig = {
    free: {
      label: 'Quick Test',
      questions: totalQuestions || 10,
      color: 'bg-blue-50 text-blue-700 border-blue-300',
      description: 'Less accurate screening',
      icon: null
    },
    comprehensive: {
      label: 'Comprehensive Test',
      questions: totalQuestions || 25,
      color: 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-purple-700 shadow-lg',
      description: 'More accurate assessment',
      icon: <CheckCircle2 className="w-5 h-5" />
    }
  }

  const badge = typeConfig[testType] || typeConfig.free

  return (
    <div className={`inline-flex items-center gap-2 px-5 py-3 rounded-lg border ${badge.color}`}>
      {badge.icon}
      <div className="text-left">
        <div className="font-bold text-base">{badge.label}</div>
        <div className="text-sm opacity-90">{badge.questions} questions • {badge.description}</div>
      </div>
    </div>
  )
}

/**
 * Test Results Component
 * Displays test results with detailed analysis
 */
const TestResults = ({
  results,
  testType = 'free',
  onRetakeTest,
  onShareResults,
  onViewDetailedAnalysis
}) => {
  if (!results) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No results available</p>
      </div>
    )
  }

  // Get score description and insights
  const getScoreInsights = (score, percentile) => {
    if (score >= 160) return {
      title: "Exceptional Intelligence",
      description: "You demonstrate rare cognitive abilities found in less than 0.01% of the population.",
      advice: "Consider pursuing advanced academic or research opportunities that challenge your exceptional abilities."
    }
    if (score >= 130) return {
      title: "Gifted Intelligence",
      description: "You show superior cognitive abilities present in only 2% of the population.",
      advice: "You would likely thrive in intellectually demanding fields and complex problem-solving roles."
    }
    if (score >= 110) return {
      title: "Above Average Intelligence",
      description: "Your cognitive abilities are stronger than most people, placing you in the top 25%.",
      advice: "You have good potential for academic success and analytical careers."
    }
    if (score >= 90) return {
      title: "Average Intelligence",
      description: "You demonstrate typical cognitive abilities shared by most of the population.",
      advice: "You have solid reasoning skills suitable for a wide range of educational and career paths."
    }
    return {
      title: "Room for Growth",
      description: "Your cognitive abilities may benefit from focused development and practice.",
      advice: "Consider engaging in activities that challenge your reasoning and problem-solving skills."
    }
  }

  const {
    iqScore,
    correctAnswers,
    totalQuestions,
    percentile,
    categoryBreakdown = {},
    timeCompleted,
    classification,
    mode,
    percentageCorrect,
    timingAnalysis = {},
    difficultyAnalysis = {}
  } = results

  const insights = getScoreInsights(iqScore, percentile)

  // Format time
  const formatTime = (milliseconds) => {
    if (!milliseconds || isNaN(milliseconds)) {
      return '0m 0s'
    }
    const minutes = Math.floor(milliseconds / 60000)
    const seconds = Math.floor((milliseconds % 60000) / 1000)
    return `${minutes}m ${seconds}s`
  }

  // Enhanced score color system for wider range
  const getScoreColor = (score) => {
    if (score >= 180) return 'text-purple-600' // Exceptionally Gifted
    if (score >= 160) return 'text-indigo-600' // Extremely Gifted
    if (score >= 145) return 'text-blue-600'   // Highly Gifted
    if (score >= 130) return 'text-success-600' // Moderately Gifted
    if (score >= 120) return 'text-teal-600'   // Superior
    if (score >= 110) return 'text-primary-600' // High Average
    if (score >= 90) return 'text-gray-600'    // Average
    if (score >= 80) return 'text-orange-600'  // Low Average
    if (score >= 70) return 'text-yellow-600'  // Borderline
    if (score >= 55) return 'text-red-500'     // Below Average
    return 'text-red-700'                      // Significantly Below Average
  }

  return (
    <div className="max-w-4xl mx-auto pt-4">
      {/* Main Results Card */}
      <Card className="mb-6 text-center">
        <CardHeader>
          <div className="mb-2">
            <Trophy className="w-16 h-16 mx-auto text-warning-500 mb-2" />
            <CardTitle className="text-3xl mb-2">
              Your IQ Score
            </CardTitle>
            <div className="flex justify-center mb-3">
              <TestTypeLabel testType={testType} totalQuestions={totalQuestions} />
            </div>
            <CardDescription>
              {testType === 'comprehensive' ? 'Comprehensive' : 'Quick'} Assessment Results
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="mb-8">
            <div className={`text-6xl font-bold mb-2 ${getScoreColor(iqScore)}`}>
              {iqScore}
            </div>
            <div className="text-xl text-gray-600 mb-4">
              {classification || 'Average'}
            </div>
            <div className="text-lg text-gray-500">
              Better than {percentile || 50}% of test takers
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="text-center">
              <Target className="w-8 h-8 mx-auto text-primary-500 mb-2" />
              <div className="text-2xl font-semibold text-gray-900">
                {correctAnswers}/{totalQuestions}
              </div>
              <div className="text-sm text-gray-500">Correct Answers</div>
            </div>
            
            <div className="text-center">
              <TrendingUp className="w-8 h-8 mx-auto text-success-500 mb-2" />
              <div className="text-2xl font-semibold text-gray-900">
                {percentageCorrect || Math.round((correctAnswers / totalQuestions) * 100)}%
              </div>
              <div className="text-sm text-gray-500">Accuracy</div>
            </div>
            
            <div className="text-center">
              <Clock className="w-8 h-8 mx-auto text-warning-500 mb-2" />
              <div className="text-2xl font-semibold text-gray-900">
                {formatTime(timeCompleted)}
              </div>
              <div className="text-sm text-gray-500">Time Taken</div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={onShareResults} variant="outline">
              <Share2 className="w-4 h-4 mr-2" />
              Share Results
            </Button>
            
            <Button onClick={onRetakeTest}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Retake Test
            </Button>
            
            {testType === 'comprehensive' && (
              <Button onClick={onViewDetailedAnalysis} variant="secondary">
                View Analysis
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Category Breakdown (for comprehensive test) */}
      {testType === 'comprehensive' && Object.keys(categoryBreakdown).length > 0 && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Performance by Category</CardTitle>
            <CardDescription>
              See how you performed in different cognitive areas
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <div className="space-y-4">
              {Object.entries(categoryBreakdown).map(([category, percentage]) => (
                <div key={category}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-gray-700 capitalize">
                      {category.replace('_', ' ')}
                    </span>
                    <span className="text-sm text-gray-500">
                      {percentage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-primary-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle>What's Next?</CardTitle>
          <CardDescription>
            Recommendations based on your performance
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <div className="space-y-4">
            {testType === 'free' && (
              <div className="p-4 bg-primary-50 rounded-lg">
                <h4 className="font-medium text-primary-700 mb-2">
                  Get More Detailed Results
                </h4>
                <p className="text-sm text-primary-600 mb-3">
                  Take our comprehensive test for detailed category analysis and percentile ranking.
                </p>
                <Button size="sm" variant="outline">
                  Upgrade to Comprehensive
                </Button>
              </div>
            )}
            
            <div className="p-4 bg-success-50 rounded-lg">
              <h4 className="font-medium text-success-700 mb-2">
                Practice and Improve
              </h4>
              <p className="text-sm text-success-600">
                Regular practice can help improve your cognitive abilities. Try taking the test again in a few weeks.
              </p>
            </div>
            
            <div className="p-4 bg-warning-50 rounded-lg">
              <h4 className="font-medium text-warning-700 mb-2">
                Share Your Achievement
              </h4>
              <p className="text-sm text-warning-600">
                Share your results with friends and challenge them to beat your score!
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default TestResults